import type { ProductShortKeyType, VerifyPaymentReq } from '@/api/payments';
import type { VerifySubscriptionReq } from '@/api/payments/verifySubscription';
import type { Notification } from '@/common';

export type PrefillOptions = {
  name: string;
  email: string;
  contact: string;
};

export type PaymentMethods =
  | 'card'
  | 'upi'
  | 'netbanking'
  | 'paylater'
  | 'cardlessemi'
  | 'app'
  | 'paylater'
  | 'credicardemi'
  | 'wallet';

export type PaymentMethodConfig = Array<{
  method: PaymentMethods;
  type?: Array<'debit' | 'collect' | 'intent' | 'qr'>;
}>;

export type RazorpayPaymentMethodConfig = {
  methods: Array<PaymentMethods>;
};

export type RazorpayPaymentOptions = {
  key: string;
  amount?: number;
  subscription_id?: string;
  currency?: 'INR';
  recurring?: string;
  customer_id?: string;
  name: string;
  description: string;
  image: string;
  order_id?: string;
  handler: (response: RazorPaySuccessResponse) => void;
  notes?: {
    address: string;
  };
  prefill?: PrefillOptions;
  theme?: {
    color: string;
  };
  config: any;
  retry: {
    enabled: boolean;
  };
};

export type RazorPaySuccessResponse = {
  razorpay_payment_id: string;
  razorpay_order_id: string;
  razorpay_signature: string;
};

/**
 * Provides an event handler for All the payments
 *
 * @param {number} amount - Amount to be processed
 * @param {string} razorpayKey - Razorpay key id generated while creating an order
 * @param {string} pgOrderId - Payment Gateway Orderid
 * @param {string} description - Payment Description
 * @param {Function} handlePaymentSuccess - A callback to handle payment success
 * @param {Function} handlePaymentFailure - A callback to handle payment failure
 * @param {Function} addNotification - add Notification function from useNotificationStore
 * @param {Function} postPaymentDetailsMutate - mutation to post details to BE after successful payment
 * @param {Function} verifySubscription - mutation to verify the auto-pay subcription created
 * @param {string} orderId - order id of the created order
 * @param {string} customerId - customer ID of which the order is created
 * @param {ProductShortKeyType} productShortKey - product short key
 * @param {PaymentMethodConfig} paymentConfig - Payment configuration/methods
 * @param {PrefillOptions} prefillOptions - prefill options for Name, Phone, and Email
 * @param {pgCustomerId} pgCustomerId - pgCustomerId from subscription API to pass onto SIP
 */
const PaymentsProvider = (
  amount: number,
  razorpayKey: string,
  pgOrderId: string,
  description: string,
  handlePaymentSuccess: (result: RazorPaySuccessResponse) => void,
  handlePaymentFailure: (result: any) => void,
  addNotification: (notification: Omit<Notification, 'id'>) => void,
  postPaymentDetailsMutate: (props: {
    orderId: string;
    customerId: string;
    payload: VerifyPaymentReq;
  }) => Promise<any>,
  verifySubscription: (props: {
    subscriptionId: string;
    customerId: string;
    payload: VerifySubscriptionReq;
  }) => Promise<any>,
  orderId: string,
  customerId: string,
  productShortKey: ProductShortKeyType,
  paymentConfig: PaymentMethodConfig,
  subscriptionId?: string,
  pgCustomerId?: string,
  prefillOptions?: PrefillOptions,
  retry?: boolean
) => {
  const onPaymentSuccess = async ({
    razorpay_order_id,
    razorpay_payment_id,
    razorpay_signature,
  }: RazorPaySuccessResponse) => {
    let response: any;

    if (subscriptionId) {
      response = await verifySubscription({
        subscriptionId,
        customerId,
        payload: {
          orderId: razorpay_order_id,
          paymentId: razorpay_payment_id,
          signature: razorpay_signature,
        },
      });
    } else {
      response = await postPaymentDetailsMutate({
        orderId,
        customerId,
        payload: {
          orderId: razorpay_order_id,
          paymentId: razorpay_payment_id,
          secret: razorpay_signature,
          productShortKey,
        },
      });
    }
    if (response.status == 200)
      handlePaymentSuccess({
        razorpay_order_id,
        razorpay_payment_id,
        razorpay_signature,
      });
  };
  const paymentHandler = async () => {
    if (!amount || !razorpayKey || !pgOrderId) return;
    /**
     * Intializes Razorpay SDK
     * @returns {Promise<any>}
     */
    const initializeRazorpay = (): Promise<any> => {
      return new Promise((resolve) => {
        const script = document.createElement('script');
        script.src = 'https://checkout.razorpay.com/v1/checkout.js';

        script.onload = () => {
          resolve(true);
        };
        script.onerror = () => {
          resolve(false);
        };

        document.body.appendChild(script);
      });
    };

    let options: RazorpayPaymentOptions;

    if (subscriptionId !== undefined && subscriptionId !== null) {
      options = {
        key: razorpayKey, // Enter the Key ID generated from the Dashboard
        amount, // Amount is in currency subunits. Default currency is INR. Hence, 50000 refers to 50000 paise
        currency: 'INR',
        customer_id: pgCustomerId,
        recurring: '1',
        name: 'Muthoot FinCorp ONE', // your business name
        description,
        image: 'https://example.com/your_logo',
        order_id: pgOrderId, // This is a sample Order ID. Pass the `id` obtained in the response of Step 1
        // subscription_id: subscriptionId,
        handler(response: RazorPaySuccessResponse) {
          addNotification({
            title: 'Payment Successful!',
            type: 'success',
            message: 'Your payment has been made.',
          });

          onPaymentSuccess({ ...response });
        },
        prefill: prefillOptions,
        notes: {
          address: 'Razorpay Corporate Office',
        },
        theme: {
          color: '#083F8D',
        },
        config: {
          display: {
            blocks: {
              banks: {
                name: 'Payment Methods',
                instruments: paymentConfig,
              },
            },
            sequence: ['block.banks'],
            preferences: {
              show_default_blocks: false,
            },
          },
        },
        retry: {
          enabled: retry ?? false,
        },
      };
    } else {
      options = {
        key: razorpayKey, // Enter the Key ID generated from the Dashboard
        amount,
        order_id: pgOrderId, // This is a sample Order ID. Pass the `id` obtained in the response of Step 1
        name: 'Muthoot FinCorp ONE',
        currency: 'INR',
        image: 'https://example.com/your_logo',
        description,
        handler(response: RazorPaySuccessResponse) {
          addNotification({
            title: 'Payment Successful!',
            type: 'success',
            message: 'Your payment has been made.',
          });

          onPaymentSuccess({ ...response });
        },
        prefill: prefillOptions,
        notes: {
          address: 'Razorpay Corporate Office',
        },
        theme: {
          color: '#083F8D',
        },
        config: {
          display: {
            blocks: {
              banks: {
                name: 'Payment Methods',
                instruments: paymentConfig,
              },
            },
            sequence: ['block.banks'],
            preferences: {
              show_default_blocks: false,
            },
          },
        },
        retry: {
          enabled: retry ?? false,
        },
      };
    }

    const res = await initializeRazorpay();
    if (!res) {
      return;
    }

    const razorpayInstance = new (window as any).Razorpay(options);
    razorpayInstance.on('payment.failed', (response: any) => {
      addNotification({
        title: `Payment failed: ${response.error.code}`,
        type: 'error',
        message: `${response.error.description}`,
      });
      handlePaymentFailure({ ...response });
    });
    razorpayInstance.open();
  };

  paymentHandler();
};

export default PaymentsProvider;
